import type { CheckboxProps } from '@mui/material'
import moment from 'moment'
import * as R from 'ramda'
import type { ControlButtonGroupItem, Nil, User } from '@pbt/pbt-ui-components'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import { PrescriptionType } from '~/constants/prescription'
import { OrderType } from '~/constants/SOAPStates'
import type {
  ChargeSheetItemSection,
  InventoryItem,
  LabTest,
  Order,
  OrderFilter,
  Price,
  Task,
  Variation,
} from '~/types'
import type { InvoiceLineItem } from '~/types/entities/finance'
import { getHighValue, getIsRetailOrderLineItem } from '~/utils/invoiceUtils'

import { getPrescriptionType } from './prescription'

const KEY_PROPS: (keyof Order)[] = [
  'price',
  'inventory',
  'procedure',
  'labTest',
  'variation',
  'globalInventory',
  'globalVariation',
  'globalInventoryMapping',
  'globalVariationMapping',
  'taskTemplate',
]

export enum OrderStateKeys {
  INVENTORY_LOG_STATUS = 'inventoryLogStatusId',
  STATE = 'stateId',
}

export const CheckboxColor: Record<string, CheckboxProps['color']> = {
  PRIMARY: 'primary',
  DEFAULT: 'default',
}

export type GetItemColorCallback = (order: Order) => keyof typeof CheckboxColor

const isEqual = (item: Order, logItem: Order) => {
  const keyPropsEqual = KEY_PROPS.every((name) => {
    const getId = R.path([name, 'id'])
    return !logItem[name] || getId(item) === getId(logItem)
  })

  const parentsEqual = !item.parentId || item.parentId === logItem.parentId

  return keyPropsEqual && parentsEqual
}

export const findOrderItemByItem = (
  item: Order,
  list: Order[],
  withoutRefills = false,
) => {
  const arr = list.filter(
    (logItem) => isEqual(item, logItem) && (!withoutRefills || !logItem.parent),
  )
  // Get the last element; there can be cases where we have several identical lab tests in the list, as we can add the same test after declining the previous one
  return R.last(arr)
}

export const getOrderWithNotes = (
  order: Order,
  selectedOrders: Order[],
): Order => {
  const logItem = findOrderItemByItem(order, selectedOrders)
  return logItem
    ? {
        ...order,
        id: logItem.id,
        notes: logItem.notes,
        vaccinationDetails: logItem.vaccinationDetails,
        charge: logItem.charge,
        cover: logItem.cover,
      }
    : order
}

export const getOrderStateKey = (prescriptionType: PrescriptionType | Nil) =>
  getPrescriptionType(prescriptionType)?.isInHouse
    ? OrderStateKeys.INVENTORY_LOG_STATUS
    : OrderStateKeys.STATE
export const isOrderType = (order: Order, type: OrderType) =>
  order.type === type
export const isBundle = (order: Order) => isOrderType(order, OrderType.BUNDLE)
export const isProcedure = (order: Order) =>
  isOrderType(order, OrderType.PROCEDURE)
export const isImagingProcedure = (order: Order) =>
  order?.procedure?.category === 'Imaging'
export const isReminder = (order: Order) =>
  isOrderType(order, OrderType.REMINDER)
export const isWellnessPlanItem = (order: Order) =>
  isOrderType(order, OrderType.WPLANS) || isOrderType(order, OrderType.BENEFIT)
export const isLabTest = (order: Order) =>
  isOrderType(order, OrderType.LAB_TEST)
export const isTask = (order: Order) => isOrderType(order, OrderType.TASK)
export const isObsoleteGlobalItem = (
  order: Order,
  isFoodCatalogEnabled: boolean,
) =>
  isOrderType(order, OrderType.INVENTORY) &&
  order.inventory === undefined &&
  order[isFoodCatalogEnabled ? 'globalVariationMapping' : 'globalVariation']
    ?.obsolete === true

export const getCreateDefaultQuantityDrugExpDate = () =>
  moment().add(1, 'year').toISOString()

export const getInventoryItemCategoryId = (
  item: any,
  isFoodCatalogEnabled: boolean,
) =>
  item?.inventory?.categoryId ||
  item?.[isFoodCatalogEnabled ? 'globalInventoryMapping' : 'globalInventory']
    ?.categoryId ||
  item?.categoryId ||
  item?.inventoryCategoryId

export const hasButtons = (
  buttons: (ControlButtonGroupItem | boolean | Nil)[],
) => !R.isEmpty(buttons?.filter(R.identity))

export const isLabTestUnderOrder = (order: Order | Nil) =>
  Boolean(order?.labTest?.labOrderId)

export const getMissingPdmpReportingFields = (
  prescription: Order,
  client: User | Nil,
) => {
  const needPdmpReporting =
    prescription?.inventory?.pdmpReport &&
    getPrescriptionType(prescription.prescriptionType)?.isInHouse

  if (!needPdmpReporting) {
    return {}
  }

  return R.filter((item) => R.identity(item), {
    missingNdcNumber: !prescription.variation?.nationalDrugCode,
    missingClientAddress: [
      client?.address,
      client?.country,
      client?.city,
      client?.state,
      client?.zip,
    ].some(R.not),
    missingClientPhoneNumber: !client?.mobilePhone,
    missingClientGender: !client?.genderId,
    missingClientDOB: !client?.dateOfBirth,
  })
}

export const isOrderTask = (task: Task, order: Order) =>
  task?.orderId === order.id && task?.orderType === order.type
export const isOrder = (item: any) => Boolean(OrderType[item.type as OrderType])
export const getUniqueOrderId = (order: Order) => `${order.id}-${order.type}`
export const getOrderByUniqueId = (orderUniqueId: string, orders: Order[]) =>
  orders.find((order) => getUniqueOrderId(order) === orderUniqueId)

const RABIES_VACCINATION_CODE = '50001'

export const getIsRabiesVaccine = (order: Order) =>
  order?.procedure?.procedureCode === RABIES_VACCINATION_CODE

export const zeroUsedInvoiceItemExists = (
  appointmentLineItemsMap: Record<string, InvoiceLineItem> | Nil,
  item: Order | Nil,
) => {
  const priceId = item?.price?.id
  const lineItem =
    appointmentLineItemsMap && priceId
      ? appointmentLineItemsMap[priceId]
      : undefined

  return lineItem?.prepaid && !lineItem?.usedQuantity
}

export const getItemColor =
  (
    appointmentLineItemsMap: Record<string, InvoiceLineItem> | Nil,
    selectedOrders: Order[],
  ) =>
  (item: Order) => {
    const isSelected =
      findOrderItemByItem(item, selectedOrders || []) !== undefined
    const hasZeroUsedInInvoice = zeroUsedInvoiceItemExists(
      appointmentLineItemsMap,
      item,
    )
    return hasZeroUsedInInvoice && !isSelected
      ? CheckboxColor.DEFAULT
      : CheckboxColor.PRIMARY
  }

const isDefaultColor = R.equals(CheckboxColor.DEFAULT)
const prioritizeCheckboxColor = (hasDefaults: boolean) =>
  hasDefaults ? CheckboxColor.DEFAULT : CheckboxColor.PRIMARY
export const aggregateCheckboxColor = (
  getSingleItemColor: (order: Order) => CheckboxProps['color'],
) =>
  R.pipe(
    R.map(getSingleItemColor),
    R.any(isDefaultColor),
    prioritizeCheckboxColor,
  )

export const calculateAvailableQuantity = (order: Order) =>
  order?.prepaidBox
    ? parseFloat(`${order.prepaidBox?.remaining || 0}`) +
      parseFloat(`${order?.quantity || 0}`)
    : null

export const dropOrderPrepaidBox = (order: Order) => ({
  ...R.omit(['prepaid', 'usedQuantity'], order),
  quantity: 1,
})

const fieldsToOmit = ['items', 'usageCount']
export const getOrdersEqual = (orderOne: Order, orderTwo: Order) =>
  R.equals(R.omit(fieldsToOmit, orderOne), R.omit(fieldsToOmit, orderTwo))

export const hasOrderFilter = (type: OrderType, filters: OrderFilter[]) =>
  filters.some((filter) => filter.type === type)

export const hasPrice = (order: Order) => R.hasPath(['price', 'price'], order)

export const hasPriceOrAggregatedPrice = (
  order: Order,
  isSetBundlePriceEnabled = false,
) =>
  hasPrice(order) ||
  R.any(hasPrice, order.items || []) ||
  (isSetBundlePriceEnabled &&
    order.type === OrderType.BUNDLE &&
    order.fixedPrice != null)

export const getIsRefill = (order: Order) =>
  Boolean(order?.parentId) || order.type === OrderType.REFILL

export const isSoapOutsidePharmacyOrder = ({
  soapId,
  invoiceId,
}: Pick<Order, 'soapId' | 'invoiceId'>) => soapId && !invoiceId

export const hasSelectedAllFromBundle = (
  order: Order,
  isCheckedItem: (item: Order) => boolean,
) => {
  const bundleItems = order.items || []
  return bundleItems.length > 0 && bundleItems.every(isCheckedItem)
}

export const getItemId = (
  item: Pick<
    Order,
    | 'price'
    | 'variation'
    | 'globalVariation'
    | 'globalInventory'
    | 'globalVariationMapping'
    | 'globalInventoryMapping'
  >,
) =>
  item.variation?.id ||
  item.globalVariationMapping?.id ||
  item.globalInventoryMapping?.id ||
  item.globalVariation?.id ||
  item.globalInventory?.id ||
  item.price.id

type UsedQuantityOptions = {
  isEstimate: boolean
  isPrePaidEnabled: boolean
}

export const dropUsedQuantity = (order: Order) =>
  R.omit(['usedQuantity'], order)

export const normalizeOrderUsedQuantity = (
  prepaidValue: number,
  order: Order,
  { isEstimate, isPrePaidEnabled }: UsedQuantityOptions,
) => {
  // Estimates don't have usedQuantity
  if (isEstimate) {
    return dropUsedQuantity(order)
  }

  // Invoices don't have range value at all, so we pick just the high value
  return {
    ...order,
    usedQuantity: isPrePaidEnabled
      ? prepaidValue
      : getHighValue(order.quantity || 1),
  }
}

export const addOrderInitialUsedQuantity = (
  order: Order,
  options: UsedQuantityOptions,
) => {
  const hasPrepaid = order.prepaid && options.isPrePaidEnabled
  const itemQuantity = order.quantity || 1
  const normalizedOrder = normalizeOrderUsedQuantity(0, order, options)

  return {
    ...normalizedOrder,
    quantity: itemQuantity,
    prepaid: hasPrepaid,
  }
}

export const doSelectedChargesHaveConflictingAutoships = (
  selectedSections: string[],
  chargeSheetItemsMap: Record<string, ChargeSheetItemSection>,
  isAutoshipEnabled: boolean,
) => {
  // No FT, all set
  if (!isAutoshipEnabled) return false

  // Get selected charge sheet sections based on the selected section IDs
  const selectedChargeSheetSections = Object.values(chargeSheetItemsMap).filter(
    (section) => selectedSections.includes(section.id),
  )

  // Flatten the groupedItems from selected sections
  const selectedItems = selectedChargeSheetSections.flatMap(
    (section) => section.groupedItems,
  ) as (InvoiceLineItem | RetailOrderLineItem)[]

  // Filter for Retail Order Line Items with autoship details
  const autoshipItems = selectedItems.filter(
    (item) =>
      getIsRetailOrderLineItem(item) &&
      item.autoshipFrequency &&
      item.autoshipUnit,
  ) as RetailOrderLineItem[]

  // If no autoship items, all set
  if (autoshipItems.length === 0) return false

  // Get the autoship details of the first item
  const { autoshipUnit: initialUnit, autoshipFrequency: initialFrequency } =
    autoshipItems[0]

  // Check if all autoship items have the same unit ID and frequency
  return autoshipItems.some(
    (item) =>
      item.autoshipUnit?.id !== initialUnit?.id ||
      item.autoshipFrequency !== initialFrequency,
  )
}

export function convertInvoiceLineItemToSoapLog(
  invoiceLog: InvoiceLineItem,
): Order {
  const inventory: InventoryItem = {
    id: '',
    activeIngredient: '',
    ageMinimum: 0,
    ageUnitId: '',
    brandName: '',
    business: '',
    categoryId: '',
    compoundable: false,
    controlledSubstanceScheduleId: '',
    creationDate: '',
    description: '',
    genderRestrictions: [],
    globalInventoryItemId: '',
    globalInventoryItemMappingId: '',
    controlled: invoiceLog.inventoryControlled,
    modificationDate: '',
    name: '',
    pdmpReport: false,
    requiresPrescription: false,
    speciesIds: [],
    subcategoryId: '',
    variations: [],
    weightMinimum: 0,
  }

  const variation: Variation = {
    onHandStatusId: '',
    categoryId: '',
    subcategoryId: '',
    active: false,
    businessVariationIds: [],
    creationDate: '',
    description: '',
    globalVariationId: '',
    globalInventoryVariationMappingId: '',
    id: '',
    inStockAmount: '',
    inStockUnitsId: '',
    inventoryItemId: '',
    location: '',
    maxQuantity: 0,
    maxQuantityUnitId: '',
    modificationDate: '',
    name: '',
    nationalDrugCode: '',
    obsolete: false,
    onHandAmount: 1,
    onHandUnitsId: '',
    orderStatusId: '',
    prices: [],
    rabiesTagExpiration: '',
    rabiesTagExpirationOptionId: '',
    rabiesTagExpirationUnitId: '',
    reorderPoint: 1,
    reorderPointUnitId: '',
    reorderQuantity: 1,
    reorderQuantityUnitId: '',
    strengthColorId: '',
    strengthCombo: '',
    strengthUnitsId: '',
    vaccineDefaultAmount: 0,
    vaccineDefaultAmountId: '',
    vaccineDefaultDeliveryLocation: '',
    vaccineDefaultDeliveryLocationId: '',
    vaccineDefaultDoseTypeId: '',
    vaccineDeliveryMethodId: '',
    vaccineDurationOfImmunity: '',
    vaccineDurationOfImmunityOptionId: '',
    vaccineDurationOfImmunityUnitId: '',
    vaccineTypeId: '',
    vetcoveName: '',
    vetcoveSku: '',
    weightMaximum: 1,
    weightMinimum: 0,
    deliveryMethodId: invoiceLog.variationDeliveryMethodId,
    formId: invoiceLog.variationFormId,
    customForm: invoiceLog.variationCustomForm || '',
    perPackageUnitsId: invoiceLog.variationPerPackageUnitsId || '',
    strength: invoiceLog.variationStrength,
    perPackageAmount: invoiceLog.variationPerPackageAmount,
    packageTypeId: invoiceLog.variationPackageTypeId || '',
    count: 0,
    saleUnitOfMeasure: '',
  }

  return {
    ...R.omit(['logType'], invoiceLog),
    id: invoiceLog.logId,
    lineItem: invoiceLog,
    type: invoiceLog.logType,
    items: (invoiceLog.items || []).map(convertInvoiceLineItemToSoapLog),
    labTest: {
      id: invoiceLog.priceItemId,
      type: invoiceLog.labTestType,
      name: invoiceLog.name,
      vendorId: R.defaultTo(null)(invoiceLog.labTestVendorId),
      labOrderId: invoiceLog.labTestOrderId,
      vendorCode: invoiceLog.labTestVendorCode,
      testFor: invoiceLog.labTestTestFor,
      stateId: R.defaultTo(null)(
        invoiceLog.stateId || invoiceLog.stateEntity?.id,
      ),
      notes: invoiceLog.notes,
      bundleId: invoiceLog.bundleId,
      prepaid: invoiceLog.prepaid,
      specimen: invoiceLog.labTestSpecimen,
    } as LabTest,
    price: {
      id: invoiceLog.priceId,
      price: invoiceLog.price,
      taxable: invoiceLog.priceTaxable,
      discountAllowed: invoiceLog.discountAllowed,
      minCharge: invoiceLog.minCharge,
      cost: invoiceLog.cost,
      costUnitsSize: invoiceLog.costUnitsSize,
      dispensingFee: invoiceLog.dispensingFee,
      priceUnitsSize: invoiceLog.priceUnitsSize,
      unitId: invoiceLog.unitId,
      timeUnitId: invoiceLog.priceTimeUnitId,
      minTimeUnitSize: invoiceLog.priceMinTimeUnitSize,
      maxTimeUnitSize: invoiceLog.priceMaxTimeUnitSize,
      timeUnitSize: invoiceLog.priceTimeUnitSize,
    } as Price,
    soapId: invoiceLog.soapId,
    quantity: invoiceLog.quantity,
    usedQuantity: invoiceLog.usedQuantity,
    prepaidBox: {
      remaining: invoiceLog.prepaidRemaining,
    },
    prescriptionType: invoiceLog.prescriptionType,
    procedure: {
      id: invoiceLog.logId,
      procedureCode: invoiceLog.procedureCode,
      description: invoiceLog.procedureDescription,
      modalityId: invoiceLog.procedureModalityId,
    },
    inventory,
    variation,
    modificationDate: invoiceLog.soapLogModificationDate,
    stateId: invoiceLog.stateId || invoiceLog.stateEntity?.id,
  }
}

export function convertRetailOrderLineItemToSoapLog(
  invoiceLog: RetailOrderLineItem,
): Order {
  const inventory: InventoryItem = {
    id: '',
    activeIngredient: '',
    ageMinimum: 0,
    ageUnitId: '',
    brandName: '',
    business: '',
    categoryId: '',
    compoundable: false,
    controlledSubstanceScheduleId: '',
    creationDate: '',
    description: '',
    genderRestrictions: [],
    globalInventoryItemId: '',
    globalInventoryItemMappingId: '',
    controlled: false,
    modificationDate: '',
    name: '',
    pdmpReport: false,
    requiresPrescription: false,
    speciesIds: [],
    subcategoryId: '',
    variations: [],
    weightMinimum: 0,
  }

  const variation: Variation = {
    onHandStatusId: '',
    categoryId: '',
    subcategoryId: '',
    active: false,
    businessVariationIds: [],
    creationDate: '',
    description: '',
    globalVariationId: '',
    globalInventoryVariationMappingId: '',
    id: '',
    inStockAmount: '',
    inStockUnitsId: '',
    inventoryItemId: '',
    location: '',
    maxQuantity: 0,
    maxQuantityUnitId: '',
    modificationDate: '',
    name: '',
    nationalDrugCode: '',
    obsolete: false,
    onHandAmount: 1,
    onHandUnitsId: '',
    orderStatusId: '',
    prices: [],
    rabiesTagExpiration: '',
    rabiesTagExpirationOptionId: '',
    rabiesTagExpirationUnitId: '',
    reorderPoint: 1,
    reorderPointUnitId: '',
    reorderQuantity: 1,
    reorderQuantityUnitId: '',
    strengthColorId: '',
    strengthCombo: '',
    strengthUnitsId: '',
    vaccineDefaultAmount: 0,
    vaccineDefaultAmountId: '',
    vaccineDefaultDeliveryLocation: '',
    vaccineDefaultDeliveryLocationId: '',
    vaccineDefaultDoseTypeId: '',
    vaccineDeliveryMethodId: '',
    vaccineDurationOfImmunity: '',
    vaccineDurationOfImmunityOptionId: '',
    vaccineDurationOfImmunityUnitId: '',
    vaccineTypeId: '',
    vetcoveName: '',
    vetcoveSku: '',
    weightMaximum: 1,
    weightMinimum: 0,
    deliveryMethodId: null,
    formId: null,
    customForm: '',
    perPackageUnitsId: '',
    strength: null,
    perPackageAmount: null,
    packageTypeId: '',
    count: 0,
    saleUnitOfMeasure: '',
  }

  return {
    ...R.omit(['logType'], invoiceLog),
    id: invoiceLog.id,
    lineItem: invoiceLog,
    type: OrderType.PRESCRIPTION,
    items: undefined,
    price: {
      price: invoiceLog.price,
    } as Price,
    soapId: invoiceLog.soap?.id,
    quantity: invoiceLog.quantity,
    usedQuantity: 0,
    prepaidBox: null,
    prescriptionType: PrescriptionType.CHEWY,
    procedure: undefined,
    inventory,
    variation,
    modificationDate: invoiceLog.modificationDate,
    stateId: invoiceLog.logState?.id || '',
  }
}

// Helper functions for convertOrderToInvoiceLineItem
export const getCost = (item: Order): number =>
  R.path(['lineItem', 'cost'], item) || R.path(['price', 'cost'], item) || 0

export const getGroupName = (item: Order): string =>
  R.path(['lineItem', 'groupName'], item) || item.group || ''

export const getCreationDate = (item: Order): string =>
  item.creationDate ||
  R.path(['variation', 'creationDate'], item) ||
  R.path(['inventory', 'creationDate'], item) ||
  ''
const getCostUnitsSize = (item: Order): number =>
  R.path(['price', 'costUnitsSize'], item) || 0

const getDeclined = (item: Order): boolean =>
  R.path(['lineItem', 'declined'], item) || false

const getDiscountAllowed = (item: Order): boolean =>
  R.path(['price', 'discountAllowed'], item) || false

const getDiscountAmount = (item: Order): number | null =>
  R.path(['lineItem', 'discountAmount'], item) || null

const getDiscountPerc = (item: Order): number | null =>
  R.path(['lineItem', 'discountPerc'], item) || null

const getDispensingFee = (item: Order): number =>
  R.path(['price', 'dispensingFee'], item) || 0

const getExtendedPrice = (item: Order): number =>
  R.path(['lineItem', 'extendedPrice'], item) || 0

const getLabTestOrderId = (item: Order): string =>
  R.path(['labTest', 'labOrderId'], item) || ''

const getLabTestSpecimen = (item: Order): string =>
  R.path(['labTest', 'specimen'], item) || ''

const getLabTestTestFor = (item: Order): string =>
  R.path(['labTest', 'testFor'], item) || ''

const getLabTestType = (item: Order): string =>
  R.path(['labTest', 'type'], item) || ''

const getLabTestVendorCode = (item: Order): string =>
  R.path(['labTest', 'vendorCode'], item) || ''

const getLabTestVendorId = (item: Order): string =>
  R.path(['labTest', 'vendorId'], item) || ''

const getLabTestVendorName = (item: Order): string =>
  R.path(['labTest', 'vendorName'], item) || ''

const getLogId = (item: Order): string => R.path(['lineItem', 'id'], item) || ''

const getMinCharge = (item: Order): number | null =>
  R.path(['price', 'minCharge'], item) || null

const getModificationDate = (item: Order): string => item.modificationDate || ''

const getPrepaidRemaining = (item: Order): number =>
  R.path(['prepaidBox', 'remaining'], item) || 0

export const getPriceId = (item: Order): string => R.path(['price', 'id'], item)

const getPriceItemId = (item: Order): string =>
  R.path(['labTest', 'priceItemId'], item) || ''

const getPriceMaxTimeUnitSize = (item: Order): number =>
  R.path(['price', 'maxTimeUnitSize'], item) || 0

const getPriceMinTimeUnitSize = (item: Order): number =>
  R.path(['price', 'minTimeUnitSize'], item) || 0

export const getPriceTaxable = (item: Order): boolean =>
  R.path(['price', 'taxable'], item)

const getPriceTimeUnitId = (item: Order): string | undefined =>
  R.path(['price', 'timeUnitId'], item)

const getPriceTimeUnitSize = (item: Order): number =>
  R.path(['price', 'timeUnitSize'], item) || 0

export const getPriceUnits = (item: Order): string | undefined =>
  R.path(['lineItem', 'priceUnits'], item)

export const getPriceUnitsSize = (item: Order): number | null =>
  R.path(['price', 'priceUnitsSize'], item)

const getProcedureCategoryId = (item: Order): string =>
  R.path(['procedure', 'procedureCategoryId'], item) || ''

const getProcedureCode = (item: Order): string =>
  R.path(['procedure', 'procedureCode'], item) || ''

export const getProcedureDescription = (item: Order): string | undefined =>
  R.path(['procedure', 'description'], item)

const getProcedureModalityId = (item: Order): string =>
  R.path(['procedure', 'modalityId'], item) || ''

const getProcedureOrderId = (item: Order): string =>
  R.path(['lineItem', 'procedureOrderId'], item) || ''

const getQuantity = (item: Order): number => item.quantity || 0

const getSubTotal = (item: Order): number | undefined =>
  R.path(['lineItem', 'subTotal'], item)

const getTaxAmount = (item: Order): number | undefined =>
  R.path(['lineItem', 'taxAmount'], item)

const getTaxRate = (item: Order): number | undefined =>
  R.path(['lineItem', 'taxRate'], item)

const getUnitId = (item: Order): string =>
  R.path(['price', 'unitId'], item) || ''

const getUpdatedAt = (item: Order): string =>
  R.path(['lineItem', 'updatedAt'], item) || ''

const getVariationCustomForm = (item: Order): string | undefined =>
  R.path(['variation', 'customForm'], item)

const getVariationDeliveryMethodId = (item: Order): string | undefined =>
  R.path(['variation', 'deliveryMethodId'], item)

const getVariationFormId = (item: Order): string | undefined =>
  R.path(['variation', 'formId'], item)

const getVariationPackageTypeId = (item: Order): string | undefined =>
  R.path(['variation', 'packageTypeId'], item)

const getVariationPerPackageAmount = (item: Order): number | undefined =>
  R.path(['variation', 'perPackageAmount'], item)

const getVariationPerPackageUnitsId = (item: Order): string | undefined =>
  R.path(['variation', 'perPackageUnitsId'], item)

const getVariationStrength = (item: Order): number | undefined =>
  R.path(['variation', 'strength'], item)

const getVariationStrengthUnitsId = (item: Order): string | undefined =>
  R.path(['variation', 'strengthUnitsId'], item)

const getWplanLogId = (item: Order): string =>
  R.path(['lineItem', 'wplanLogId'], item) || ''

const getInventoryControlled = (item: Order): boolean =>
  Boolean(R.path(['inventory', 'controlled'], item))

const getTaxed = (item: Order): boolean =>
  Boolean(R.path(['lineItem', 'taxed'], item))

export const convertOrderToInvoiceLineItem = (
  item: Order,
  isFoodCatalogEnabled: boolean,
): InvoiceLineItem => ({
  bundleId: item.bundleId || null,
  categoryId: getInventoryItemCategoryId(item, isFoodCatalogEnabled),
  clientId: item.clientId || '',
  cost: getCost(item),
  costUnitsSize: getCostUnitsSize(item),
  creationDate: getCreationDate(item),
  declined: getDeclined(item),
  discountAllowed: getDiscountAllowed(item),
  discountAmount: getDiscountAmount(item),
  discountPerc: getDiscountPerc(item),
  dispensingFee: getDispensingFee(item),
  extendedPrice: getExtendedPrice(item),
  fixedPrice:
    item.fixedPrice ??
    (item.lineItem && 'fixedPrice' in item.lineItem
      ? item.lineItem.fixedPrice
      : null) ??
    null,
  group: item.group || '',
  groupName: getGroupName(item),
  id: getLogId(item),
  inventoryControlled: getInventoryControlled(item),
  invoiceId: item.invoiceId || '',
  items: R.map(
    (itm: Order) => convertOrderToInvoiceLineItem(itm, isFoodCatalogEnabled),
    R.propOr([], 'items', item),
  ),
  labTestOrderId: getLabTestOrderId(item),
  labTestSpecimen: getLabTestSpecimen(item),
  labTestTestFor: getLabTestTestFor(item),
  labTestType: getLabTestType(item),
  labTestVendorCode: getLabTestVendorCode(item),
  labTestVendorId: getLabTestVendorId(item),
  labTestVendorName: getLabTestVendorName(item),
  logId: item.id,
  logType: item.type,
  minCharge: getMinCharge(item),
  modificationDate: getModificationDate(item),
  name: item.name,
  notes: item.notes || '',
  orderNotes: item.notes || '',
  origin: item.origin,
  patientId: item.patientId || '',
  prepaid: Boolean(item.prepaid),
  prepaidRemaining: getPrepaidRemaining(item),
  prescriptionType: item.prescriptionType,
  price: item.price,
  priceId: getPriceId(item),
  priceItemId: getPriceItemId(item),
  priceMaxTimeUnitSize: getPriceMaxTimeUnitSize(item),
  priceMinTimeUnitSize: getPriceMinTimeUnitSize(item),
  priceTaxable: getPriceTaxable(item),
  priceTimeUnitId: getPriceTimeUnitId(item),
  priceTimeUnitSize: getPriceTimeUnitSize(item),
  priceUnits: getPriceUnits(item),
  priceUnitsSize: getPriceUnitsSize(item),
  procedureCategoryId: getProcedureCategoryId(item),
  procedureCode: getProcedureCode(item),
  procedureDescription: getProcedureDescription(item),
  procedureModalityId: getProcedureModalityId(item),
  procedureOrderId: getProcedureOrderId(item),
  quantity: getQuantity(item),
  soapId: item.soapId || '',
  state: item.stateId || '',
  stateId: item.stateId || '',
  subTotal: getSubTotal(item),
  taxAmount: getTaxAmount(item),
  taxRate: getTaxRate(item),
  taxed: getTaxed(item),
  unitId: getUnitId(item),
  updatedAt: getUpdatedAt(item),
  usedQuantity: item.usedQuantity || null,
  variationCustomForm: getVariationCustomForm(item),
  variationDeliveryMethodId: getVariationDeliveryMethodId(item),
  variationFormId: getVariationFormId(item),
  variationPackageTypeId: getVariationPackageTypeId(item),
  variationPerPackageAmount: getVariationPerPackageAmount(item),
  variationPerPackageUnitsId: getVariationPerPackageUnitsId(item),
  variationStrength: getVariationStrength(item),
  variationStrengthUnitsId: getVariationStrengthUnitsId(item),
  wplanLogId: getWplanLogId(item),
})
